import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface DateFormatContextType {
  dateFormat: string;
  setDateFormat: (format: string) => void;
  formatDate: (date: Date) => string;
  parseDate: (dateString: string) => Date | null;
  getDateInputFormat: () => string;
}

const DateFormatContext = createContext<DateFormatContextType | undefined>(undefined);

export const useDateFormat = () => {
  const context = useContext(DateFormatContext);
  if (!context) {
    throw new Error('useDateFormat must be used within a DateFormatProvider');
  }
  return context;
};

interface DateFormatProviderProps {
  children: ReactNode;
}

export const DateFormatProvider: React.FC<DateFormatProviderProps> = ({ children }) => {
  const [dateFormat, setDateFormat] = useState<string>('dd-mm-yyyy');

  useEffect(() => {
    const savedFormat = localStorage.getItem('dateFormat');
    if (savedFormat) {
      setDateFormat(savedFormat);
    }
  }, []);

  const updateDateFormat = (format: string) => {
    setDateFormat(format);
    localStorage.setItem('dateFormat', format);
  };

  const formatDate = (date: Date): string => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    switch (dateFormat) {
      case 'mm-dd-yyyy':
        return `${month}-${day}-${year}`;
      case 'yyyy-mm-dd':
        return `${year}-${month}-${day}`;
      case 'dd-mm-yyyy':
      default:
        return `${day}-${month}-${year}`;
    }
  };

  const parseDate = (dateString: string): Date | null => {
    if (!dateString) return null;

    let day: number, month: number, year: number;
    const parts = dateString.split('-');
    
    if (parts.length !== 3) return null;

    switch (dateFormat) {
      case 'mm-dd-yyyy':
        month = parseInt(parts[0], 10);
        day = parseInt(parts[1], 10);
        year = parseInt(parts[2], 10);
        break;
      case 'yyyy-mm-dd':
        year = parseInt(parts[0], 10);
        month = parseInt(parts[1], 10);
        day = parseInt(parts[2], 10);
        break;
      case 'dd-mm-yyyy':
      default:
        day = parseInt(parts[0], 10);
        month = parseInt(parts[1], 10);
        year = parseInt(parts[2], 10);
        break;
    }

    if (isNaN(day) || isNaN(month) || isNaN(year)) return null;
    if (month < 1 || month > 12) return null;
    if (day < 1 || day > 31) return null;

    return new Date(year, month - 1, day, 12, 0, 0, 0);
  };

  const getDateInputFormat = (): string => {
    switch (dateFormat) {
      case 'mm-dd-yyyy':
        return 'MM-DD-YYYY';
      case 'yyyy-mm-dd':
        return 'YYYY-MM-DD';
      case 'dd-mm-yyyy':
      default:
        return 'DD-MM-YYYY';
    }
  };

  return (
    <DateFormatContext.Provider value={{
      dateFormat,
      setDateFormat: updateDateFormat,
      formatDate,
      parseDate,
      getDateInputFormat
    }}>
      {children}
    </DateFormatContext.Provider>
  );
};